'use client';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import Fuse from 'fuse.js';
import { FileEdit, FileStack, PlusCircle, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Firm, FirmStatus } from '@/services/types/firms-types';

// Filter component
interface FilterComponentProps {
  searchValue: string;
  statusFilter: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onClearFilters: () => void;
  onCreateClick: () => void;
}

const FilterComponent = ({
  searchValue,
  statusFilter,
  onSearchChange,
  onStatusChange,
  onClearFilters,
  onCreateClick,
}: FilterComponentProps) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white p-3 mb-4 border-b border-gray-100">
      <div className="flex flex-col sm:flex-row gap-2 items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              type="search"
              placeholder="Search firms..."
              className="pl-9 h-9 text-sm w-full border-gray-200"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          <div className="w-full sm:w-[160px]">
            <Select value={statusFilter} onValueChange={onStatusChange}>
              <SelectTrigger
                id="status"
                className="h-9 text-sm w-full border-gray-200"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.values(FirmStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(searchValue || statusFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-9 px-2 whitespace-nowrap self-center text-gray-500"
              aria-label="Clear filters"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/templates-v2')}
            variant="outline"
            className="flex items-center gap-1.5 h-9 whitespace-nowrap px-3"
            size="sm"
          >
            <FileStack className="h-4 w-4" />
            All Templates
          </Button>
          <Button
            onClick={() => navigate('/zaddy?tab=bulk-sow')}
            variant="outline"
            className="flex items-center gap-1.5 h-9 whitespace-nowrap px-3"
            size="sm"
          >
            <FileEdit className="h-4 w-4" />
            Bulk Edit SOW
          </Button>
          <Button
            onClick={onCreateClick}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white h-9 whitespace-nowrap px-3"
            size="sm"
          >
            <PlusCircle className="h-4 w-4" />
            New Firm
          </Button>
        </div>
      </div>
    </div>
  );
};

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  searchValue: string;
  statusFilter: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onClearFilters: () => void;
  onFirmClick: (firmId: string) => void;
  onEditClick: (event: React.MouseEvent, firmId: string) => void;
  onCreateClick: () => void;
}

export const DataTable = <TData, TValue>({
  columns,
  data,
  isLoading,
  searchValue,
  statusFilter,
  onSearchChange,
  onStatusChange,
  onClearFilters,
  onFirmClick,
  onEditClick,
  onCreateClick,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [filteredData, setFilteredData] = useState<TData[]>([]);

  // Set up Fuse.js for client-side search
  const fuse = useMemo(() => {
    return new Fuse(data as Firm[], {
      keys: ['name', 'full_name', 'email', 'primary_state'],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [data]);

  // Apply filters whenever search value or status filter changes
  useEffect(() => {
    let result = [...data] as TData[];

    // Apply search if there's a search value
    if (searchValue) {
      const searchResults = fuse.search(searchValue);
      result = searchResults.map(({ item }) => item) as TData[];
    }

    // Apply status filter if not "all"
    if (statusFilter !== 'all') {
      result = result.filter((item) => {
        const firm = item as unknown as Firm;
        return firm.status === statusFilter;
      });
    }

    setFilteredData(result);
  }, [data, searchValue, statusFilter, fuse]);

  const table = useReactTable({
    data: filteredData.length > 0 ? filteredData : data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      rowSelection,
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 rounded mb-4" />
        {[...Array(5)].map((_, index) => (
          <div key={index} className="h-16 bg-gray-100 rounded mb-2" />
        ))}
      </div>
    );
  }

  // Empty state - now also shows when filters result in no matches
  if (
    (filteredData.length === 0 && (searchValue || statusFilter !== 'all')) ||
    data.length === 0
  ) {
    return (
      <div className="space-y-4">
        <FilterComponent
          searchValue={searchValue}
          statusFilter={statusFilter}
          onSearchChange={onSearchChange}
          onStatusChange={onStatusChange}
          onClearFilters={onClearFilters}
          onCreateClick={onCreateClick}
        />

        <div className="flex flex-col items-center justify-center p-8 text-center bg-gray-50">
          <h3 className="font-medium text-lg">No firms found</h3>
          <p className="text-muted-foreground mt-1">
            {searchValue || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Get started by creating a new firm'}
          </p>
          {!searchValue && statusFilter === 'all' && (
            <Button onClick={onCreateClick} className="mt-4">
              Create Firm
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <FilterComponent
        searchValue={searchValue}
        statusFilter={statusFilter}
        onSearchChange={onSearchChange}
        onStatusChange={onStatusChange}
        onClearFilters={onClearFilters}
        onCreateClick={onCreateClick}
      />

      <div className="overflow-x-auto">
        <Table className="w-full">
          <TableHeader className="bg-white border-b border-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="h-10 py-2 px-4 text-left text-sm font-medium text-gray-700"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onFirmClick((row.original as Firm).id)}
                  className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 h-14"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="py-2 px-4 text-sm h-14 overflow-hidden"
                    >
                      <div className="truncate max-w-[250px]">
                        {cell.column.id === 'actions'
                          ? flexRender(cell.column.columnDef.cell, {
                              ...cell.getContext(),
                              onEditClick,
                            })
                          : flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-4 text-center text-sm text-gray-500"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
